import { useState, useEffect } from 'react'
import { Modal } from '../ui/Modal'
import { usePersonas } from '../../hooks/usePersonas'
import type { ContentTaskCreateRequest } from '../../lib/api'
import type { TaskDraftResponse } from '../../lib/api'

const contentTypes = [
  { value: 'blog_post', label: 'Blog Post' },
  { value: 'linkedin_post', label: 'LinkedIn Post' },
  { value: 'twitter_thread', label: 'Twitter Thread' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'facebook_post', label: 'Facebook Post' },
  { value: 'instagram_caption', label: 'Instagram Caption' },
]

const channels = [
  { value: 'blog', label: 'Blog' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'newsletter', label: 'Newsletter' },
]

export type TaskDraftPreset = 'blog' | 'linkedin' | 'newsletter' | 'podcast'

const PRESETS: Record<
  TaskDraftPreset,
  { content_type: string; channel: string; label: string; word_count_target?: number }
> = {
  blog: {
    content_type: 'blog_post',
    channel: 'blog',
    label: 'Blog',
    word_count_target: 1200,
  },
  linkedin: {
    content_type: 'linkedin_post',
    channel: 'linkedin',
    label: 'LinkedIn',
    word_count_target: 300,
  },
  newsletter: {
    content_type: 'newsletter',
    channel: 'newsletter',
    label: 'Newsletter',
    word_count_target: 800,
  },
  podcast: {
    content_type: 'blog_post',
    channel: 'blog',
    label: 'Podcast',
    word_count_target: 600,
  },
}

function draftToFormData(draft: TaskDraftResponse): Omit<ContentTaskCreateRequest, 'persona_id'> & { persona_id: string } {
  return {
    persona_id: draft.persona_id ?? '',
    content_type: draft.content_type || 'blog_post',
    channel: draft.channel || 'blog',
    topic: draft.topic || '',
    brief: draft.brief ?? '',
    keywords: draft.keywords ?? [],
    word_count_target: draft.word_count_target ?? undefined,
    include_research: draft.include_research ?? true,
    research_query: draft.research_query ?? undefined,
  }
}

export interface TaskDraftReviewModalProps {
  isOpen: boolean
  onClose: () => void
  draft: TaskDraftResponse | null
  onCreateTask: (payload: ContentTaskCreateRequest) => Promise<void>
  isLoading?: boolean
  /** When set, passed through to onCreateTask payload to link task to this conversation. */
  conversationId?: string | null
}

export function TaskDraftReviewModal({
  isOpen,
  onClose,
  draft,
  onCreateTask,
  isLoading = false,
  conversationId,
}: TaskDraftReviewModalProps) {
  const { data: personas } = usePersonas()
  const [formData, setFormData] = useState<ContentTaskCreateRequest>(() =>
    draft ? draftToFormData(draft) : {
      persona_id: '',
      content_type: 'blog_post',
      channel: 'blog',
      topic: '',
      brief: '',
      keywords: [],
      word_count_target: 800,
      include_research: true,
      research_query: '',
    }
  )
  const [keywordInput, setKeywordInput] = useState('')

  useEffect(() => {
    if (draft) {
      setFormData(draftToFormData(draft))
    }
  }, [draft])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.persona_id) return
    const payload: ContentTaskCreateRequest = { ...formData }
    if (conversationId) payload.conversation_id = conversationId
    await onCreateTask(payload)
    onClose()
  }

  const applyPreset = (preset: TaskDraftPreset) => {
    const p = PRESETS[preset]
    setFormData((prev) => ({
      ...prev,
      content_type: p.content_type,
      channel: p.channel,
      ...(p.word_count_target != null && { word_count_target: p.word_count_target }),
    }))
  }

  const addKeyword = () => {
    if (keywordInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        keywords: [...(prev.keywords || []), keywordInput.trim()],
      }))
      setKeywordInput('')
    }
  }

  const removeKeyword = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      keywords: (prev.keywords || []).filter((_, i) => i !== index),
    }))
  }

  if (!draft && isOpen) return null

  return (
    <Modal
      isOpen={isOpen && draft !== null}
      onClose={onClose}
      title="Iniciar proyecto desde conversación"
      size="lg"
    >
      <p className="text-sm text-slate-600 mb-4">
        Elige un tipo de contenido o revisa y edita los campos extraídos de la conversación.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        {(Object.keys(PRESETS) as TaskDraftPreset[]).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => applyPreset(key)}
            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 hover:border-slate-300"
          >
            {PRESETS[key].label}
          </button>
        ))}
      </div>
      {draft?.persona_hint && (
        <p className="text-sm text-slate-500 mb-4 bg-slate-50 rounded-lg p-3">
          Sugerencia de persona: {draft.persona_hint}
        </p>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Persona *</label>
          <select
            className="input"
            value={formData.persona_id}
            onChange={(e) => setFormData({ ...formData, persona_id: e.target.value })}
            required
          >
            <option value="">Seleccionar persona...</option>
            {personas?.map((persona) => (
              <option key={persona.id} value={persona.id}>
                {persona.name} ({persona.role})
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Tipo de Contenido *</label>
            <select
              className="input"
              value={formData.content_type}
              onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
              required
            >
              {contentTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Canal *</label>
            <select
              className="input"
              value={formData.channel}
              onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
              required
            >
              {channels.map((channel) => (
                <option key={channel.value} value={channel.value}>
                  {channel.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="label">Tema *</label>
          <input
            type="text"
            className="input"
            placeholder="Ej: Transformación digital en retail"
            value={formData.topic}
            onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
            required
          />
        </div>

        <div>
          <label className="label">Brief</label>
          <textarea
            className="input"
            rows={4}
            placeholder="Descripción detallada de lo que debe incluir el contenido..."
            value={formData.brief}
            onChange={(e) => setFormData({ ...formData, brief: e.target.value })}
          />
        </div>

        <div>
          <label className="label">Keywords</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              className="input flex-1"
              placeholder="Agregar keyword..."
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addKeyword()
                }
              }}
            />
            <button type="button" className="btn btn-secondary" onClick={addKeyword}>
              Agregar
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.keywords?.map((keyword, index) => (
              <span key={index} className="badge badge-info flex items-center gap-1">
                {keyword}
                <button
                  type="button"
                  onClick={() => removeKeyword(index)}
                  className="hover:text-red-600"
                >
                  x
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Palabras objetivo</label>
            <input
              type="number"
              className="input"
              value={formData.word_count_target ?? ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  word_count_target: e.target.value ? parseInt(e.target.value, 10) : undefined,
                })
              }
              min={100}
              max={5000}
            />
          </div>
          <div className="flex items-center pt-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.include_research ?? true}
                onChange={(e) => setFormData({ ...formData, include_research: e.target.checked })}
                className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
              />
              <span className="text-sm text-slate-700">Incluir investigación</span>
            </label>
          </div>
        </div>

        {formData.include_research && (
          <div>
            <label className="label">Query de investigación</label>
            <input
              type="text"
              className="input"
              placeholder="Ej: Latest trends in digital transformation 2026"
              value={formData.research_query ?? ''}
              onChange={(e) => setFormData({ ...formData, research_query: e.target.value })}
            />
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={isLoading} data-testid="draft-review-submit">
            {isLoading ? 'Creando...' : 'Crear tarea'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
